import React from 'react';

interface IRadioButtonProps {
    id?: string;
    label?: string;
    value?: string;
    checked?: boolean;
    onChange?: (value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    color?: 'blue' | 'emerald' | 'red' | 'amber' | 'purple';
    description?: string;
    error?: string;
    name?: string;
    className?: string;
}

const RadioButton: React.FC<IRadioButtonProps> = ({
    id,
    label = '',
    value = '',
    checked = false,
    onBlur,
    onChange = () => { },
    disabled = false,
    size = 'medium',
    color = 'blue',
    description,
    error,
    name,
    className = ''
}) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

    const sizes = {
        small: {
            radio: 'w-4 h-4',
            dot: 'w-2 h-2',
            text: 'text-sm',
            gap: 'gap-2'
        },
        medium: {
            radio: 'w-5 h-5',
            dot: 'w-3 h-3',
            text: 'text-base',
            gap: 'gap-3'
        },
        large: {
            radio: 'w-6 h-6',
            dot: 'w-4 h-4',
            text: 'text-lg',
            gap: 'gap-4'
        }
    };

    const colors = {
        blue: {
            checked: 'bg-[var(--v2-primary-600)] border-[var(--v2-primary-600)]',
            unchecked: 'border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card-inner)] hover:border-[var(--v2-primary-400)]',
            focus: 'focus:ring-[var(--v2-primary-500)]'
        },
        emerald: {
            checked: 'bg-emerald-600 border-emerald-600',
            unchecked: 'border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card-inner)] hover:border-emerald-400',
            focus: 'focus:ring-emerald-500'
        },
        red: {
            checked: 'bg-[var(--v2-danger)] border-[var(--v2-danger)]',
            unchecked: 'border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card-inner)] hover:border-red-400',
            focus: 'focus:ring-red-500'
        },
        amber: {
            checked: 'bg-amber-600 border-amber-600',
            unchecked: 'border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card-inner)] hover:border-amber-400',
            focus: 'focus:ring-amber-500'
        },
        purple: {
            checked: 'bg-purple-600 border-purple-600',
            unchecked: 'border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card-inner)] hover:border-purple-400',
            focus: 'focus:ring-purple-500'
        }
    };

    return (
        <div className={`flex items-start p-4 ${sizes[size].gap} ${className}`}>
            <div className="flex items-center h-6">
                <label htmlFor={radioId} className="relative cursor-pointer">
                    <input
                        type="radio"
                        id={radioId}
                        name={name}
                        value={value}
                        checked={checked}
                        onBlur={onBlur}
                        onChange={() => !disabled && onChange(value)}
                        disabled={disabled}
                        className="sr-only peer"
                    />
                    <div
                        className={`
                            ${sizes[size].radio} rounded-full border-2 flex items-center justify-center
                            transition-all duration-[var(--v2-duration-normal)] ease-[var(--v2-ease-calm)]
                            ${checked
                                ? colors[color].checked
                                : disabled
                                    ? 'border-[var(--v2-border-subtle)] bg-[var(--v2-bg-hover)]'
                                    : colors[color].unchecked
                            }
                            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                            ${error ? 'border-[var(--v2-danger)]' : ''}
                            peer-focus:ring-4 ${colors[color].focus} peer-focus:ring-offset-0
                        `}
                    >
                        {checked && (
                            <div
                                className={`
                                    ${sizes[size].dot} bg-white rounded-full
                                    transition-all duration-[var(--v2-duration-fast)] ease-[var(--v2-ease-calm)]
                                    ${disabled ? 'bg-[var(--v2-text-muted)]' : ''}
                                `}
                            />
                        )}
                    </div>
                </label>
            </div>

            {(label || description) && (
                <div className="flex-1 min-w-0">
                    {label && (
                        <label
                            htmlFor={radioId}
                            className={`
                                font-medium text-[var(--v2-text-primary)] ${sizes[size].text} leading-tight tracking-[-0.005em]
                                ${disabled ? 'text-[var(--v2-text-muted)] cursor-not-allowed' : 'cursor-pointer'}
                                ${error ? 'text-[var(--v2-danger)]' : ''}
                            `}
                        >
                            {label}
                        </label>
                    )}
                    {description && (
                        <p className="text-[var(--v2-font-secondary)] text-[var(--v2-text-secondary)] mt-1">{description}</p>
                    )}
                    {error && (
                        <p className="text-[var(--v2-font-secondary)] text-[var(--v2-danger)]/80 font-medium mt-1 flex items-center gap-1">
                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default RadioButton;
